import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { useAuthProvider } from "../Authentication/AuthProviderSwitch";
import { sendContactMessage } from "../../services/contactApi";
import { CLIENT_FLOW_STORAGE_KEYS, safeParseJson } from "./clientFlowUtils";

const defaultForm = {
  category: "support",
  priority: "medium",
  subject: "",
  message: "",
};

const ClientHelpdeskPage = () => {
  const toast = useToast();
  const { user } = useAuthProvider();
  const [form, setForm] = useState(defaultForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const companyName = useMemo(() => {
    if (typeof window === "undefined") return "";
    const company = safeParseJson(
      window.localStorage.getItem(CLIENT_FLOW_STORAGE_KEYS.COMPANY_PROFILE),
      {}
    );
    return company?.companyName || "";
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.subject.trim() || !form.message.trim()) {
      toast({ title: "Subject and message are required", status: "warning" });
      return;
    }

    setIsSubmitting(true);
    try {
      await sendContactMessage({
        name: user?.name || user?.email || "Client User",
        email: user?.email || "",
        company: companyName,
        subject: `[Client Helpdesk][${form.category.toUpperCase()}][${form.priority.toUpperCase()}] ${form.subject.trim()}`,
        message: form.message.trim(),
      });

      toast({
        title: "Helpdesk request submitted",
        description: "Our team received your request and will respond shortly.",
        status: "success",
      });

      setForm(defaultForm);
    } catch (error) {
      toast({
        title: "Submission failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Helpdesk Requests</Heading>
        <Text color="gray.600" fontSize="sm">Create a request for billing, onboarding, integration, or technical issues.</Text>
      </Box>

      <Box as="form" onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <FormControl>
            <FormLabel>Category</FormLabel>
            <Select value={form.category} onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}>
              <option value="support">General Support</option>
              <option value="billing">Billing</option>
              <option value="subscription">Subscription</option>
              <option value="integration">Integration</option>
              <option value="security">Security</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Priority</FormLabel>
            <Select value={form.priority} onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Subject</FormLabel>
            <Input value={form.subject} onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))} placeholder="Short summary of your issue" />
          </FormControl>

          <FormControl>
            <FormLabel>Details</FormLabel>
            <Textarea rows={6} value={form.message} onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))} placeholder="Describe the issue, expected behavior, and any relevant IDs/screenshots." />
          </FormControl>

          <Button type="submit" colorScheme="blue" alignSelf="start" isLoading={isSubmitting}>
            Submit Helpdesk Request
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export default ClientHelpdeskPage;
